import clsx from 'clsx';
import { type ChangeEvent, Fragment, type Ref, useState } from 'react';

const CODE_LENGTH = 6;

interface OtpCodeInputProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  // 6桁そろった時点で呼ぶ(ログインの自動送信に使う)
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/**
 * 認証アプリの6桁コードを1桁ずつのマスで表示する入力欄。
 * 入力自体は透明な1つの input で受けるので、貼り付けや OS のコード自動入力もそのまま使える
 */
export const OtpCodeInput = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  onComplete,
  disabled = false,
  autoFocus = false,
  invalid = false,
  ref,
}: OtpCodeInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const digits = value.split('');
  const activeIndex = Math.min(value.length, CODE_LENGTH - 1);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    onChange(next);
    if (next.length === CODE_LENGTH && next !== value) {
      onComplete?.(next);
    }
  };

  return (
    <div className="relative flex w-full max-w-64 items-center gap-1.5 md:max-w-78 md:gap-2">
      {Array.from({ length: CODE_LENGTH }, (_, index) => {
        const isActive = isFocused && index === activeIndex;
        return (
          <Fragment key={index}>
            {index === CODE_LENGTH / 2 && <span className="w-1 shrink-0 md:w-2" />}
            <span
              className={clsx(
                'flex h-11 min-w-0 flex-1 items-center justify-center rounded bg-white font-mono text-xl font-bold md:h-13 md:text-2xl',
                isActive ? 'border-2' : 'border',
                invalid ? 'border-red-600' : isActive ? 'border-sky-800' : 'border-neutral-400',
                disabled && 'opacity-50',
              )}
            >
              {digits[index]}
            </span>
          </Fragment>
        );
      })}
      <input
        ref={ref}
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onBlur?.();
        }}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        disabled={disabled}
        // biome-ignore lint/a11y/noAutofocus: コード入力だけの画面で使うため呼び出し側で指定する
        autoFocus={autoFocus}
        aria-invalid={invalid}
        className="absolute inset-0 h-full! cursor-text border-0! bg-transparent! text-transparent caret-transparent opacity-0"
      />
    </div>
  );
};
